// Candidate detail — info + skills + submissions/interviews + placements.
import { useState } from "react";
import type { ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { StaffingSubmissionsPanel } from "@/components/staffing-submissions-panel";
import { StaffingPlacementsPanel } from "@/components/staffing-placements-panel";
import { Trash2 } from "lucide-react";

function KV({ k, v }: { k: string; v: ReactNode }) {
  if (v === null || v === undefined || v === "" || v === false) return null;
  return (
    <div className="flex justify-between gap-4 border-b py-1.5 last:border-0">
      <span className="text-xs text-muted-foreground">{k}</span>
      <span className="text-right text-sm">{v}</span>
    </div>
  );
}
const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString() : null);

type Tab = "submissions" | "placements";

export default function CandidateDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("submissions");

  const { data, isLoading } = useQuery({
    queryKey: ["/candidates", id],
    queryFn: async () => (await api.get(`/candidates/${id}`)).data,
    enabled: !!id,
  });

  async function del() {
    if (!data || !confirm(`Delete candidate "${data.name}"?`)) return;
    await api.delete(`/candidates/${id}`);
    qc.invalidateQueries({ queryKey: ["/candidates"] });
    navigate("/candidates");
  }

  if (isLoading) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  if (!data) return <div className="p-6 text-sm text-muted-foreground">Not found.</div>;
  const c = data;

  return (
    <>
      <PageHeader
        title={c.name}
        description={`${c.benchStatus}${c.location ? ` · ${c.location}` : ""}`}
        actions={<Button variant="outline" onClick={del}><Trash2 className="mr-1 h-4 w-4" />Delete</Button>}
      />
      <div className="space-y-4 p-6">
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader><CardTitle className="text-base">Contact</CardTitle></CardHeader>
            <CardContent>
              <KV k="Email" v={c.email ? <a className="hover:underline" href={`mailto:${c.email}`}>{c.email}</a> : null} />
              <KV k="Phone" v={c.phone} />
              <KV k="LinkedIn" v={c.linkedinUrl ? <a className="hover:underline" href={c.linkedinUrl} target="_blank" rel="noreferrer">Profile</a> : null} />
              <KV k="Location" v={c.location} />
              <KV k="Timezone" v={c.timezone} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Status</CardTitle></CardHeader>
            <CardContent>
              <KV k="Bench" v={<Badge variant="secondary">{c.benchStatus}</Badge>} />
              <KV k="Visa" v={c.visaStatus} />
              <KV k="Visa expiry" v={fmtDate(c.visaExpiry)} />
              <KV k="Available from" v={fmtDate(c.availableFrom)} />
              <KV k="On bench since" v={fmtDate(c.benchStartDate)} />
              <KV k="Entity" v={c.entity} />
              <KV k="Source" v={c.source} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Skills</CardTitle></CardHeader>
            <CardContent>
              {c.skills?.length ? (
                <div className="flex flex-wrap gap-1">
                  {c.skills.map((s: any) => (
                    <Badge key={s.id} variant="outline" title={`${s.proficiency}${s.years ? ` · ${s.years}y` : ""}`}>{s.skill}</Badge>
                  ))}
                </div>
              ) : <p className="text-sm text-muted-foreground">No skills listed.</p>}
            </CardContent>
          </Card>
        </div>
        <Card>
          <div className="flex gap-1 border-b px-2 pt-2">
            {(["submissions", "placements"] as Tab[]).map((t) => (
              <button key={t} onClick={() => setTab(t)} className={`rounded-t px-3 py-1.5 text-sm ${tab === t ? "border-b-2 border-primary font-medium text-primary" : "text-muted-foreground"}`}>
                {t === "submissions" ? "Submissions" : "Placements"}
              </button>
            ))}
          </div>
          <CardContent className="pt-4">
            {tab === "submissions" && <StaffingSubmissionsPanel candidateId={c.id} />}
            {tab === "placements" && <StaffingPlacementsPanel candidateId={c.id} />}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
